export const maximizeYieldAlgorithm = async (ctx) => {
    const {
        gardenMatrix,
        plants,
        config,
        utils: { validatePlacement, placePlant, createPlantMap, fillRemainingOpenAreas }
    } = ctx;

    if (!Array.isArray(gardenMatrix) || !Array.isArray(gardenMatrix[0])) {
        throw new Error('maximizeYieldAlgorithm expects gardenMatrix to be a 2D array.');
    }

    const rows = gardenMatrix.length;
    const cols = gardenMatrix[0].length;

    let plantMap = createPlantMap(rows, cols);
    let plantsPlaced = 0;

    // Track remaining counts - use index instead of ID to handle duplicate IDs
    const plantCounts = {};
    plants.forEach((plant, index) => {
        const uniqueKey = `${plant.id}_${index}`;
        plantCounts[uniqueKey] = plant.count ?? Infinity;
    });

    // Sort plants by area (LARGEST first for better packing) and add unique keys
    const randomness = config.maximizeYieldPlantingVariance ?? 0.2; // 0 = deterministic, 1 = full random

    const sortedPlants = [...plants].map((plant, index) => ({
        ...plant,
        // Map database properties to expected properties for consistency
        name: plant.plant_name || plant.name,
        image: plant.plant_image_url || plant.image,
        veggieImage: plant.plant_image_url || plant.veggieImage,
        plantingWidth: plant.planting_width || plant.plantingWidth || 1,
        plantingHeight: plant.planting_height || plant.plantingHeight || 1,
        uniqueKey: `${plant.id}_${index}`
    })).sort((b, a) => {
        const areaA = a.plantingWidth * a.plantingHeight + Math.random() * randomness;
        const areaB = b.plantingWidth * b.plantingHeight + Math.random() * randomness;
        return areaA - areaB;
    });

    // Try to place plants at each position
    for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
        for (let colIndex = 0; colIndex < cols; colIndex++) {
            for (const plant of sortedPlants) {
                const remaining = plantCounts[plant.uniqueKey];
                if (remaining <= 0) continue;

                if (validatePlacement(plant, plantMap, gardenMatrix, colIndex, rowIndex)) {
                    plantMap = placePlant(plantMap, plant, rowIndex, colIndex); // Note the flip here!
                    plantCounts[plant.uniqueKey]--;
                    plantsPlaced++;
                    break;
                }
            }
        }
    }

    // Collect unused plants with remaining count
    const unusedPlants = sortedPlants
        .filter(p => plantCounts[p.uniqueKey] > 0)
        .map(p => ({
            name: p.name, // Use the mapped name property
            id: p.id,
            remaining: plantCounts[p.uniqueKey],
            plantingWidth: p.plantingWidth,
            plantingHeight: p.plantingHeight
        }));

    // if autofill remaining empty plots is checked by user fill with filler plants
    let filledMap = plantMap;
    if (config.autoFillEmpty) {
        filledMap = await fillRemainingOpenAreas(gardenMatrix, plantMap, config, plants);
    }

    return {
        plantMap: filledMap,
        plantsPlaced,
        unusedPlants,
        warnings: [],
        efficiency: plantsPlaced / (rows * cols)
    };
};